((root, factory) => {
  const continuationApi = typeof module === 'object' && module.exports ? require('./dim-context-continuation.js') : root?.DIMContextContinuation;
  const api = factory(continuationApi);
  if (typeof module === 'object' && module.exports) module.exports = api;
  else {
    root.DIMRuntimeBootstrap = api;
    const element = root.document.getElementById('dim-runtime-context');
    if (!element) return;
    try {
      const config = JSON.parse(element.textContent);
      if (config.runtime?.environment === 'development' && !root.DIMDevSession) throw new Error('DEV_SESSION_UNAVAILABLE');
      root.DIMRuntime = api.create(config, {
        contexts: root.DIMRuntimeContext,
        scenes: root.DIMSceneStorage,
        paidOperations: root.DIMPaidOperation,
        devSessions: root.DIMDevSession,
        storage: root.sessionStorage,
        history: root.history,
        indexedDB: root.indexedDB,
        onAuthenticated: () => root.document.dispatchEvent(new CustomEvent('dim:runtime-authenticated')),
        onVerified: () => root.document.dispatchEvent(new CustomEvent('dim:runtime-verified')),
        fetchImpl: root.fetch.bind(root), pagePath: root.location.pathname, pageSearch: root.location.search, pageOrigin: root.location.origin
      });
      const mount = () => {
        const container = root.document.querySelector('main') || root.document.body;
        if (container && root.DIMRuntime?.devSession) {
          const notice = root.DIMRuntime.devSession.mountNotice({ container });
          if (!root.DIMRuntime.devSession.getToken()) notice.show();
        }
      };
      if (root.document.readyState === 'loading') root.document.addEventListener('DOMContentLoaded', mount, { once: true });
      else mount();
    } catch (error) {
      // A requested successor journey must fail closed, never adopt legacy/live state.
      root.DIMRuntime = Object.freeze({ error, verified: false, enabled: true });
    }
  }
})(typeof window !== 'undefined' ? window : globalThis, continuations => {
  'use strict';
  const copy = value => JSON.parse(JSON.stringify(value));
  const fail = code => { const error = new Error(code); error.code = code; throw error; };
  const contextKeys = ['storefrontId', 'bindingId', 'mode', 'purpose'];
  const sameContext = (a, b) => contextKeys.every(key => typeof a?.[key] === 'string' && a[key] === b?.[key]);

  function create(config, { contexts, scenes, paidOperations, devSessions, storage, indexedDB, history, continuations: configuredContinuations = continuations, fetchImpl = fetch, now = Date.now, pagePath = '/', pageSearch = '', pageOrigin = null, onAuthenticated, onVerified } = {}) {
    if (config?.enabled !== true) return null;
    if (!contexts || !configuredContinuations || !config.runtime) fail('RUNTIME_UNCONFIGURED');
    const runtime = contexts.create(copy(config.runtime));
    let previewContinuation = null;
    if (!config.context && config.staffPreviewShell === true && runtime.environment === 'development') {
      // A private draft needs no published routing metadata. The neutral shell
      // only locates an exact selector or an explicitly prepared signed handoff;
      // it never supplies customer authority or imports a primary draft.
      const shell = { storefrontId: 'dim-main', bindingId: 'primary', mode: 'primary', purpose: 'customer' };
      previewContinuation = configuredContinuations.create({ runtime, context: shell, storage, history, now, pagePath, pageSearch });
      if (previewContinuation.provisionalContext?.purpose === 'staff-preview') {
        config = { ...config, context: shell, pageBinding: { bindingId: 'primary', pageKey: '/' } };
      } else { previewContinuation.dispose(); previewContinuation = null; }
    }
    if (!config.context) {
      // Permit the existing development sign-in/health controls before a fresh
      // primary catalogue has been selected. No draft, paid adapter or customer
      // request is created until the page carries a real bound context.
      if (runtime.environment !== 'development' || config.context !== null || config.pageBinding !== null || !devSessions
        || typeof pageOrigin !== 'string') fail('RUNTIME_UNCONFIGURED');
      let origin;
      try { origin = new URL(pageOrigin); } catch (_) { fail('RUNTIME_UNCONFIGURED'); }
      if (origin.protocol !== 'https:' || origin.origin !== pageOrigin) fail('RUNTIME_UNCONFIGURED');
      let disposed = false;
      const devSession = devSessions.create({ runtime, shop: config.shop, fetchImpl, now });
      const reject = async () => fail(disposed ? 'RUNTIME_DISPOSED' : 'CONTEXT_UNCONFIGURED');
      return Object.freeze({ enabled: true, runtime, devSession, context: null, scene: null, verified: false,
        features: Object.freeze({}), fontCatalogue: Object.freeze([]), verify: reject, authenticatedFetch: reject,
        requireAdmission: reject, prepareTransition: reject, prepareJourneyTransition: reject,
        invalidate() {}, dispose() { if (disposed) return; disposed = true; devSession.dispose(); } });
    }
    const originalContext = copy(config.context);
    const pageBinding = config.pageBinding;
    if (!contextKeys.every(key => typeof originalContext[key] === 'string' && originalContext[key])) fail('CONTEXT_UNCONFIGURED');
    configuredContinuations.validateProvisionalContext(originalContext, 'CONTEXT_UNCONFIGURED');
    if (!pageBinding || typeof pageBinding !== 'object' || Array.isArray(pageBinding) || Object.keys(pageBinding).length !== 2
      || typeof pageBinding.bindingId !== 'string' || !pageBinding.bindingId || pageBinding.bindingId !== originalContext.bindingId
      || typeof pageBinding.pageKey !== 'string' || !pageBinding.pageKey || pageBinding.pageKey.length > 120
      || /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/.test(pageBinding.pageKey)
      || typeof pageOrigin !== 'string') fail('CONTEXT_UNCONFIGURED');
    let renderedOrigin;
    try { renderedOrigin = new URL(pageOrigin); } catch (_) { fail('CONTEXT_UNCONFIGURED'); }
    if (renderedOrigin.protocol !== 'https:' || renderedOrigin.origin !== pageOrigin || renderedOrigin.username || renderedOrigin.password || renderedOrigin.hash) fail('CONTEXT_UNCONFIGURED');
    const workerOrigin = new URL(runtime.workerOrigin).origin;
    if (workerOrigin !== runtime.workerOrigin || !workerOrigin.startsWith('https://')) fail('DESTINATION_MISMATCH');
    const timingEnabled = runtime.environment === 'development' && runtime.themeId === '205891830103';
    const timingPaths = new Set(['/v1/environment', '/v1/storefront/context', '/v1/storefront/admit', '/v1/profile/resolve', '/v1/product/resolve']);
    const timingStart = () => { try { return timingEnabled ? now() : null; } catch (_) { return null; } };
    const timing = (stage, startedAt, path = null, status = null, error = null) => {
      if (!timingEnabled || !Number.isFinite(startedAt)) return;
      try {
        const at = now();
        if (!Number.isFinite(at)) return;
        const record = { stage, at, elapsedMs: Math.max(0, at - startedAt) };
        if (path !== null) record.path = timingPaths.has(path) ? path : 'protected-request';
        if (Number.isInteger(status) && status >= 100 && status <= 599) record.status = status;
        if (error !== null) record.errorCode = typeof error?.code === 'string' && /^[A-Z][A-Z0-9_]{0,79}$/.test(error.code)
          ? error.code : error?.name === 'AbortError' ? 'REQUEST_ABORTED' : 'REQUEST_FAILED';
        console.debug('[DIM timing]', JSON.stringify(record));
      } catch (_) { /* Diagnostics must never affect request or authority ownership. */ }
    };
    let disposed = false;
    let epoch = 0;
    let inFlight = null;
    let verifiedUntil = 0;
    let verifiedCapabilities = Object.freeze([]);
    let verifiedDisplay = null;
    let activeContext = Object.freeze(originalContext);
    let journeyAuthority = null;
    // Recovery binds the exact signed record receipt, which may differ from
    // the current page's freshly issued receipt for the same stable authority.
    let journeyRecoveryAuthority = null;
    let negotiatedReuse = false;
    let authorityGeneration = 0;
    let refreshInFlight = null;
    let failedAutomaticRefresh = null;
    let acquisitionTail = Promise.resolve();
    const retainedSelections = new Map();
    const admissionInFlight = new Map();
    const continuation = previewContinuation || configuredContinuations.create({ runtime, context: originalContext, storage, history, now, pagePath, pageSearch });
    const journeyKey = `${continuation.key}:journey-data-reuse:v1`;
    const supportsReuse = () => verifiedCapabilities.includes('journey-data-reuse@1');
    const usableAuthority = () => !disposed && supportsReuse() && journeyAuthority && journeyAuthority.expiresAt > now();
    const routeKey = route => route === 'create' ? 'create_with_ai' : route;
    const requestSelection = selection => ({ ...selection, route: routeKey(selection.route || 'create') === 'create_with_ai' ? 'create' : selection.route });
    const selectionKey = selection => {
      if (!selection || typeof selection.variantId !== 'string' || !selection.variantId || selection.variantId.length > 120
        || selection.route !== undefined && !['upload', 'create', 'create_with_ai'].includes(selection.route)) fail('CURRENT_ADMISSION_REQUIRED');
      return JSON.stringify([selection.variantId, routeKey(selection.route || 'create'), selection.treatment || 'default']);
    };
    const safeProjection = (value, { display = false } = {}) => {
      const encoded = JSON.stringify(value);
      if (encoded.length > (display ? 65536 : 262144) || /(?:data:|blob:|[?&](?:token|signature)=)/i.test(encoded)
        || !display && /https?:/i.test(encoded)
        || /"(?:prompt|image_data_url|turnstile_token|authorization|requestToken)"/i.test(encoded)) fail('INVALID_RETAINED_SELECTION');
      return copy(value);
    };
    const persistJourney = () => {
      if (!usableAuthority()) return;
      const record = { schemaVersion: 'journey-data-reuse@1', context: activeContext, authority: journeyAuthority,
        authorityGeneration, capabilities: verifiedCapabilities, display: null, selections: [...retainedSelections.values()],
        recoveryAuthority: journeyRecoveryAuthority };
      // Existing server-verified display assets are safe to retain. Authority,
      // prompts, image payloads and transport credentials never enter this view.
      try { record.display = verifiedDisplay ? safeProjection(verifiedDisplay, { display: true }) : null; } catch (_) {}
      try {
        const encoded = JSON.stringify(record);
        if (encoded.length <= 1_048_576) storage.setItem(journeyKey, encoded);
      } catch (_) { /* Current memory and the previous recovery record remain available. */ }
    };
    const retainSelection = (selection, projection, { persist = true } = {}) => {
      if (!usableAuthority()) return null;
      const key = selectionKey(selection);
      if (projection?.allowed !== true || !configuredContinuations.sameAuthorityIdentity(activeContext, projection.context)
        || !projection.resolution || projection.resolution.shopify_variant_id !== selection.variantId
        || routeKey(projection.resolution.route) !== routeKey(selection.route || 'create')
        || projection.selection?.variantId !== selection.variantId
        || typeof projection.selection.productId !== 'string' || !projection.selection.productId || projection.selection.productId.length > 120
        || selection.productId !== undefined && projection.selection.productId !== selection.productId
        || selection.options !== undefined && JSON.stringify(selection.options) !== JSON.stringify(projection.selection.options)) fail('INVALID_RETAINED_SELECTION');
      const value = safeProjection({ allowed: true, context: activeContext, selection: { ...projection.selection,
        route: selection.route || 'create', treatment: selection.treatment || projection.selection.treatment || 'default' },
        resolution: projection.resolution, ...(projection.textPlacement === undefined ? {} : { textPlacement: projection.textPlacement }) });
      if (retainedSelections.size >= 2000 && !retainedSelections.has(key)) retainedSelections.delete(retainedSelections.keys().next().value);
      retainedSelections.set(key, value);
      if (persist) persistJourney();
      return copy(value);
    };
    const getRetainedSelection = selection => {
      if (!usableAuthority()) return null;
      const value = retainedSelections.get(selectionKey(selection));
      if (!value || selection.productId !== undefined && value.selection.productId !== selection.productId
        || selection.options !== undefined && JSON.stringify(selection.options) !== JSON.stringify(value.selection.options)) return null;
      return copy(value);
    };
    const admissionResult = value => {
      const result = copy(value);
      if (result.selection) {
        // Route/treatment belong to the retained authority selector. Consumers
        // keep the existing exact selection contract used by Guided handoffs.
        const selected = result.selection;
        result.selection = { productId: selected.productId, variantId: selected.variantId,
          options: selected.options === undefined ? [] : copy(selected.options) };
      }
      return Object.freeze(result);
    };
    const acceptJourney = (payload, { allowExpired = false, persist = true } = {}) => {
      if (!supportsReuse() || payload?.journeyAuthority === undefined) return;
      const authority = payload.journeyAuthority;
      if (!authority || Object.keys(authority).sort().join(',') !== 'expiresAt,reference,schemaVersion'
        || authority.schemaVersion !== 'journey-authority@1' || typeof authority.reference !== 'string'
        || !authority.reference || authority.reference.length > 16384 || /[\r\n]/.test(authority.reference)
        || !Number.isSafeInteger(authority.expiresAt) || authority.expiresAt <= 0 || !allowExpired && authority.expiresAt <= now()
        || authority.expiresAt > now() + 7_260_000) fail('INVALID_JOURNEY_AUTHORITY');
      if (payload.context && !configuredContinuations.sameAuthorityIdentity(activeContext, payload.context)) fail('CONTEXT_MISMATCH');
      if (journeyAuthority?.reference === authority.reference && journeyAuthority.expiresAt !== authority.expiresAt) fail('INVALID_JOURNEY_AUTHORITY');
      const previousAuthority = journeyAuthority, previousUntil = verifiedUntil, previousRecovery = journeyRecoveryAuthority;
      const previousSelections = new Map(retainedSelections);
      try {
        for (const [key, value] of retainedSelections) {
          if (!configuredContinuations.sameAuthorityIdentity(activeContext, value.context)) retainedSelections.delete(key);
        }
        journeyAuthority = Object.freeze(copy(authority));
        verifiedUntil = authority.expiresAt;
        if (payload.retainedSelections !== undefined && (!Array.isArray(payload.retainedSelections) || payload.retainedSelections.length > 2000
          || JSON.stringify(payload.retainedSelections).length > 1_048_576)) fail('INVALID_RETAINED_SELECTION');
        const projections = payload.retainedSelections !== undefined ? payload.retainedSelections
          : payload.allowed === true && payload.selection && payload.resolution ? [payload] : null;
        journeyRecoveryAuthority = previousAuthority?.reference === authority.reference ? previousRecovery : null;
        if (Array.isArray(projections)) {
          let exactContext = null;
          const keys = [];
          for (const value of projections) {
            configuredContinuations.validateContext(value?.context, 'INVALID_RETAINED_SELECTION');
            if (!configuredContinuations.sameAuthorityIdentity(activeContext, value.context)
              || exactContext && (!configuredContinuations.sameAuthorityIdentity(exactContext, value.context) || exactContext.receipt !== value.context.receipt)) fail('INVALID_RETAINED_SELECTION');
            exactContext = value.context;
            keys.push(selectionKey(value.selection));
          }
          if (exactContext && journeyRecoveryAuthority && journeyRecoveryAuthority.context.receipt !== exactContext.receipt) fail('INVALID_JOURNEY_AUTHORITY');
          journeyRecoveryAuthority = exactContext ? Object.freeze({ reference: authority.reference, context: copy(exactContext), selectionKeys: [...new Set(keys)] }) : null;
        }
        if (payload.retainedSelections !== undefined) {
          for (const value of payload.retainedSelections) retainSelection(value.selection, value, { persist: false });
        }
        if (payload.allowed === true && payload.selection && payload.resolution) retainSelection({ ...payload.selection,
          route: payload.selection.route || (payload.resolution.route === 'upload' ? 'upload' : 'create') }, payload, { persist: false });
      } catch (error) {
        journeyAuthority = previousAuthority; journeyRecoveryAuthority = previousRecovery; verifiedUntil = previousUntil; retainedSelections.clear();
        for (const [key, value] of previousSelections) retainedSelections.set(key, value);
        throw error;
      }
      if (persist) persistJourney();
    };
    const restoreJourney = () => {
      try {
        const raw = storage.getItem(journeyKey);
        if (!raw || raw.length > 1_048_576) return;
        const record = JSON.parse(raw);
        if (record.schemaVersion !== 'journey-data-reuse@1' || !Array.isArray(record.capabilities)
          || !record.capabilities.includes('journey-data-reuse@1') || record.capabilities.some(value => typeof value !== 'string')
          || !configuredContinuations.sameBaseContext(continuation.provisionalContext || originalContext, record.context)) return;
        configuredContinuations.validateContext(record.context, 'CONTEXT_MISMATCH');
        if (continuation.provisionalContext?.schemaVersion && !configuredContinuations.sameAuthorityIdentity(continuation.provisionalContext, record.context)) return;
        const previousContext = activeContext;
        activeContext = Object.freeze(copy(record.context));
        verifiedCapabilities = Object.freeze(record.capabilities.slice());
        negotiatedReuse = true;
        if (Number.isSafeInteger(record.authorityGeneration) && record.authorityGeneration >= 0) authorityGeneration = record.authorityGeneration;
        verifiedDisplay = record.display ? safeProjection(record.display, { display: true }) : null;
        try {
          acceptJourney({ journeyAuthority: record.authority, retainedSelections: record.selections, context: record.context }, { allowExpired: true, persist: false });
          // Older local snapshots do not establish the signed receipt join.
          // Existing original records keep their own already captured proof.
          journeyRecoveryAuthority = null;
          const recovery = record.recoveryAuthority;
          if (recovery && recovery.reference === record.authority.reference && Array.isArray(recovery.selectionKeys)
            && recovery.selectionKeys.length <= 2000 && recovery.selectionKeys.every(key => typeof key === 'string' && retainedSelections.has(key))) {
            configuredContinuations.validateContext(recovery.context, 'INVALID_RETAINED_SELECTION');
            if (!configuredContinuations.sameAuthorityIdentity(activeContext, recovery.context)) fail('INVALID_RETAINED_SELECTION');
            journeyRecoveryAuthority = Object.freeze(copy(recovery));
          }
          continuation.saveVerified({ context: record.context });
        } catch (error) { activeContext = previousContext; throw error; }
      } catch (_) { journeyAuthority = null; journeyRecoveryAuthority = null; retainedSelections.clear(); verifiedUntil = 0; verifiedDisplay = null; verifiedCapabilities = Object.freeze([]); negotiatedReuse = false; }
    };
    const requireCurrent = captured => { if (disposed || captured !== epoch) fail('RUNTIME_DISPOSED'); };
    const controllers = new Set();
    const invalidate = () => { epoch++; authorityGeneration++; failedAutomaticRefresh = null; journeyAuthority = null; journeyRecoveryAuthority = null; retainedSelections.clear(); try { storage.removeItem(journeyKey); } catch (_) {} verifiedUntil = 0; verifiedDisplay = null; verifiedCapabilities = Object.freeze([]); inFlight = null; controllers.forEach(controller => controller.abort()); };
    const devSession = runtime.environment === 'development' && devSessions
      ? devSessions.create({ runtime, shop: config.shop, fetchImpl, now, onInvalidated: invalidate,
        requireStaffAccess: continuation.provisionalContext?.purpose === 'staff-preview',
        onAuthenticated: () => { invalidate(); onAuthenticated?.(); } }) : null;
    let transportSessionInFlight = null;
    const ensureTransportSession = async () => {
      // Anonymous Development bearers deliberately stay in memory. Navigation
      // may restore facts, then establish only its transport session once.
      if (!devSession?.getToken || devSession.getToken() || activeContext.purpose === 'staff-preview') return;
      if (transportSessionInFlight) return transportSessionInFlight;
      const operation = (async () => {
        const captured = epoch;
        const identity = await boundedJson('/v1/environment', {}, captured);
        contexts.verifyIdentity(identity, runtime, ['protected-drafts', 'catalogue']);
      })();
      transportSessionInFlight = operation;
      try { return await operation; } finally { if (transportSessionInFlight === operation) transportSessionInFlight = null; }
    };
    const acquireInSequence = async (invoke, signal) => {
      const previous = acquisitionTail;
      let release;
      const slot = new Promise(resolve => { release = resolve; });
      acquisitionTail = previous.catch(() => {}).then(() => slot);
      const controller = new AbortController();
      controllers.add(controller);
      const captured = epoch;
      let rejectAborted;
      const aborted = new Promise((_, reject) => { rejectAborted = () => reject(disposed || captured !== epoch
        ? Object.assign(new Error('RUNTIME_DISPOSED'), { code: 'RUNTIME_DISPOSED' }) : new DOMException('The operation was aborted.', 'AbortError')); });
      const abort = () => controller.abort();
      controller.signal.addEventListener('abort', rejectAborted, { once: true });
      signal?.addEventListener?.('abort', abort, { once: true });
      const timer = setTimeout(abort, 30000);
      try {
        if (signal?.aborted) abort();
        await Promise.race([previous, aborted]);
        requireCurrent(captured);
        if (controller.signal.aborted) throw new DOMException('The operation was aborted.', 'AbortError');
        return await Promise.race([invoke(controller.signal), aborted]);
      } finally {
        release(); clearTimeout(timer); controllers.delete(controller);
        signal?.removeEventListener?.('abort', abort);
        controller.signal.removeEventListener('abort', rejectAborted);
      }
    };
    const rawAuthenticatedFetch = async (url, options = {}, beforeDispatch = null, acquiring = false, currentPermission = false) => {
      requireCurrent(epoch);
      const parsed = new URL(url, workerOrigin);
      if (parsed.origin !== workerOrigin || parsed.username || parsed.password || parsed.hash) fail('DESTINATION_MISMATCH');
      const factRequest = negotiatedReuse && ['/v1/storefront/catalogue', '/v1/profile/resolve', '/v1/product/resolve', '/v1/storefront/admit'].includes(parsed.pathname)
        && !(parsed.pathname === '/v1/storefront/admit' && (() => { try { return Boolean(JSON.parse(options.body).action); } catch (_) { return false; } })());
      if (factRequest && !acquiring) {
        // Immutable child records extend their predecessor. Serialise only fact
        // acquisition, capturing the latest header when each request starts.
        // Paid and external-action requests never wait behind this queue.
        return acquireInSequence(signal => rawAuthenticatedFetch(url, { ...options, signal }, beforeDispatch, true, currentPermission), options.signal);
      }
      if (acquiring && usableAuthority() && !currentPermission) {
        let body;
        try { body = JSON.parse(options.body); } catch (_) {}
        const selected = parsed.pathname === '/v1/profile/resolve' && body
          ? { variantId: body.shopify_variant_id, route: body.route } : parsed.pathname === '/v1/storefront/admit' ? body?.selection : null;
        const retained = selected ? getRetainedSelection(selected) : null;
        if (retained) {
          if (parsed.pathname === '/v1/profile/resolve' && body.artwork_background_intent !== undefined
            && (body.route !== 'create_with_ai' || !retained.resolution.allowed_background_intents.includes(body.artwork_background_intent))) fail('UNSUPPORTED_ROUTE');
          if (options.signal?.aborted) throw new DOMException('The operation was aborted.', 'AbortError');
          const checked = beforeDispatch?.();
          if (checked && typeof checked.then === 'function') fail('INVALID_DISPATCH_CHECK');
          return new Response(JSON.stringify(parsed.pathname === '/v1/profile/resolve' ? { resolution: retained.resolution } : retained),
            { status: 200, headers: { 'Content-Type': 'application/json' } });
        }
      }
      if (parsed.pathname !== '/v1/environment' && negotiatedReuse) await ensureTransportSession();
      if (options.signal?.aborted) throw new DOMException('The operation was aborted.', 'AbortError');
      // The development bearer is held only in memory and attached only to the
      // exact Worker origin. Sign-in never retries a paid or earlier request.
      const headers = new Headers(options.headers || {});
      headers.set('X-DIM-Page-Path', pagePath);
      headers.set('X-DIM-Journey-Reuse', '1');
      if (journeyAuthority && parsed.pathname !== '/v1/environment') headers.set('X-DIM-Journey-Authority', journeyAuthority.reference);
      if (parsed.pathname !== '/v1/environment' && parsed.pathname.startsWith('/v1/')) {
        let grant;
        try { grant = continuation.currentGrant(); }
        catch (error) { if (!/^\/v1\/(?:dream\/recommend|prompt|image(?:\/stream)?)\/status$/.test(parsed.pathname) || error?.code !== 'CONTEXT_EXPIRED') throw error; }
        if (grant) headers.set('X-DIM-Context-Continuation', grant.grant);
      }
      const captured = epoch;
      const startedAt = timingStart();
      timing('request-start', startedAt, parsed.pathname);
      try {
        const requestOptions = { ...options, headers, credentials: 'include', redirect: 'error', cache: 'no-store' };
        let response;
        if (devSession) response = await devSession.authenticatedFetch(parsed.href, requestOptions, beforeDispatch);
        else { beforeDispatch?.(); response = await fetchImpl(parsed.href, requestOptions); }
        requireCurrent(captured);
        if (options.signal?.aborted) throw new DOMException('The operation was aborted.', 'AbortError');
        if (response.ok && supportsReuse() && !['/v1/environment', '/v1/storefront/context', '/v1/image', '/v1/image/stream', '/v1/prompt'].includes(parsed.pathname)
          && response.headers?.get?.('Content-Type')?.includes('application/json') && response.clone) {
          const payload = await response.clone().json();
          requireCurrent(captured);
          if (options.signal?.aborted) throw new DOMException('The operation was aborted.', 'AbortError');
          acceptJourney(payload);
          if (payload.journeyAuthority !== undefined && ['/v1/dream/recommend', '/v1/dream/recommend/status'].includes(parsed.pathname)) {
            // Reuse metadata belongs to this transport owner. Recommendation
            // outcomes retain their strict domain contract and correlation.
            const { journeyAuthority: consumedAuthority, retainedSelections: consumedSelections, ...outcome } = payload;
            response = new Response(JSON.stringify(outcome), {
              status: response.status, statusText: response.statusText, headers: response.headers
            });
          }
        }
        timing('request-headers', startedAt, parsed.pathname, response.status);
        return response;
      } catch (error) {
        timing('request-error', startedAt, parsed.pathname, null, error);
        throw error;
      }
    };
    const boundedJson = async (path, options, captured, currentPermission = false) => {
      const controller = new AbortController();
      controllers.add(controller);
      const timer = setTimeout(() => controller.abort(), 30000);
      const abort = () => controller.abort();
      let rejectAborted;
      const aborted = new Promise((_, reject) => { rejectAborted = () => {
        const error = disposed || captured !== epoch ? Object.assign(new Error('RUNTIME_DISPOSED'), { code: 'RUNTIME_DISPOSED' })
          : new DOMException('The operation was aborted.', 'AbortError');
        reject(error);
      }; });
      controller.signal.addEventListener('abort', rejectAborted, { once: true });
      options?.signal?.addEventListener?.('abort', abort, { once: true });
      try {
        if (options?.signal?.aborted) controller.abort();
        const response = await Promise.race([rawAuthenticatedFetch(`${workerOrigin}${path}`, { ...options, signal: controller.signal }, null, false, currentPermission), aborted]);
        requireCurrent(captured);
        if (!response.ok) {
          let body = null;
          try { body = await Promise.race([response.json(), aborted]); } catch (error) { if (controller.signal.aborted) throw error; }
          const code = typeof body?.code === 'string' && /^[A-Z0-9_]{1,80}$/.test(body.code) ? body.code
            : response.status === 401 || response.status === 403 ? 'DEV_SIGN_IN_REQUIRED' : 'DEV_SERVICE_UNAVAILABLE';
          const error = new Error(code);
          error.code = error.message;
          error.status = response.status;
          throw error;
        }
        const value = await Promise.race([response.json(), aborted]);
        requireCurrent(captured);
        return value;
      } finally {
        clearTimeout(timer);
        controllers.delete(controller);
        controller.signal.removeEventListener('abort', rejectAborted);
        options?.signal?.removeEventListener?.('abort', abort);
      }
    };
    const verify = async ({ force = false } = {}) => {
      requireCurrent(epoch);
      if (usableAuthority() || (!force || supportsReuse()) && verifiedUntil > now()) return activeContext;
      if (journeyAuthority) {
        if (failedAutomaticRefresh) throw failedAutomaticRefresh;
        return refreshAuthority({ automatic: true });
      }
      if (inFlight) return inFlight;
      const captured = epoch;
      const startedAt = timingStart();
      timing('verify-start', startedAt);
      const operation = (async () => {
        const identity = await boundedJson('/v1/environment', {}, captured);
        contexts.verifyIdentity(identity, runtime, ['protected-drafts', 'catalogue']);
        const capabilities = Object.freeze((identity.capabilities || []).filter(value => typeof value === 'string'));
        const pendingGrant = continuation.currentGrant();
        const requestContext = { origin: pageOrigin, path: pagePath, bindingId: pageBinding.bindingId, pageKey: pageBinding.pageKey };
        if (continuation.previewSelector !== null) requestContext.preview = continuation.previewSelector;
        // A staff preview grant is supplied only as the protected-request
        // header after its initial selector proof.  The primary-branded Review
        // surface therefore cannot turn an untrusted selector into authority.
        else if (pendingGrant && continuation.provisionalContext?.purpose !== 'staff-preview') requestContext.continuationGrant = pendingGrant.grant;
        const result = await boundedJson('/v1/storefront/context', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(requestContext) }, captured);
        if (!sameContext(originalContext, result.context) && continuation.provisionalContext === null) fail('CONTEXT_MISMATCH');
        configuredContinuations.validateContext(result.context, 'CONTEXT_MISMATCH');
        if (activeContext.schemaVersion === 'storefront-context@1' && !configuredContinuations.sameAuthorityIdentity(activeContext, result.context)) fail('CONTEXT_MISMATCH');
        requireCurrent(captured);
        activeContext = Object.freeze(copy(continuation.saveVerified({ context: result.context, ...(Object.prototype.hasOwnProperty.call(result, 'continuation') ? { continuation: result.continuation } : {}) })));
        if (result.display !== undefined) {
          if (!result.display || typeof result.display !== 'object' || Array.isArray(result.display) || JSON.stringify(result.display).length > 65536) fail('DISPLAY_MISMATCH');
          verifiedDisplay = copy(result.display);
        } else verifiedDisplay = null;
        verifiedUntil = now() + 30000;
        verifiedCapabilities = capabilities;
        negotiatedReuse = capabilities.includes('journey-data-reuse@1');
        acceptJourney(result);
        onVerified?.();
        timing('verified', startedAt);
        return activeContext;
      })();
      inFlight = operation;
      try { return await operation; }
      catch (error) { verifiedUntil = 0; timing('verify-error', startedAt, null, null, error); throw error; }
      finally { if (inFlight === operation) inFlight = null; }
    };
    const refreshAuthority = async ({ selection, signal, automatic = false } = {}) => {
      requireCurrent(epoch);
      if (refreshInFlight) return refreshInFlight;
      if (!journeyAuthority || !supportsReuse()) return verify({ force: true });
      const captured = epoch;
      const previous = activeContext;
      const operation = (async () => {
        const requestContext = { origin: pageOrigin, path: pagePath, bindingId: pageBinding.bindingId, pageKey: pageBinding.pageKey,
          refreshJourney: true, previousAuthority: journeyAuthority.reference, ...(selection ? { selection: {
            productId: selection.productId, variantId: selection.variantId, route: requestSelection(selection).route,
            ...(selection.treatment ? { treatment: selection.treatment } : {}) } } : {}) };
        const requestOptions = { method: 'POST', signal, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(requestContext) };
        let result;
        try { result = await boundedJson('/v1/storefront/context', requestOptions, captured); }
        catch (error) {
          // Only this unconsumed refresh can renew an expired anonymous cookie.
          // An issue/execute response never triggers an automatic replay.
          if (runtime.environment !== 'production' || error.status !== 401
            || !['PUBLIC_SESSION_EXPIRED', 'PUBLIC_SESSION_REQUIRED', 'PUBLIC_SESSION_INVALID'].includes(error.code)) throw error;
          const identity = await boundedJson('/v1/environment', { signal }, captured);
          contexts.verifyIdentity(identity, runtime, ['protected-drafts', 'catalogue', 'journey-data-reuse@1']);
          result = await boundedJson('/v1/storefront/context', requestOptions, captured);
        }
        requireCurrent(captured);
        if (!result.journeyAuthority) fail('CURRENT_JOURNEY_AUTHORITY_REQUIRED');
        if (result.display !== undefined && (!result.display || typeof result.display !== 'object' || Array.isArray(result.display)
          || JSON.stringify(result.display).length > 65536)) fail('DISPLAY_MISMATCH');
        configuredContinuations.validateContext(result.context, 'CONTEXT_MISMATCH');
        if (!configuredContinuations.sameBaseContext(previous, result.context)) fail('CONTEXT_MISMATCH');
        // Validate the envelope and projections before changing the continuation
        // owner; failed recovery leaves the complete old design recoverable.
        const previousAuthority = journeyAuthority;
        const previousRecovery = journeyRecoveryAuthority;
        const previousSelections = new Map(retainedSelections);
        const previousUntil = verifiedUntil;
        activeContext = Object.freeze(copy(result.context));
        try {
          // Refresh selects a replacement record even when its context revision
          // is unchanged. Only that record's explicit projections may survive.
          retainedSelections.clear();
          acceptJourney(result, { persist: false });
          continuation.saveVerified({ context: result.context, refreshFrom: previous,
            ...(Object.prototype.hasOwnProperty.call(result, 'continuation') ? { continuation: result.continuation } : {}) });
        } catch (error) {
          activeContext = previous; journeyAuthority = previousAuthority; journeyRecoveryAuthority = previousRecovery; verifiedUntil = previousUntil;
          retainedSelections.clear(); for (const [key, value] of previousSelections) retainedSelections.set(key, value);
          persistJourney(); throw error;
        }
        // Fence responses and synchronous owner callbacks from the prior scope.
        epoch++;
        authorityGeneration++;
        admissionInFlight.clear();
        if (result.display !== undefined) verifiedDisplay = copy(result.display);
        persistJourney();
        onVerified?.();
        return activeContext;
      })();
      refreshInFlight = operation;
      try { const result = await operation; failedAutomaticRefresh = null; return result; }
      catch (error) { if (automatic) failedAutomaticRefresh = error; throw error; }
      finally { if (refreshInFlight === operation) refreshInFlight = null; }
    };
    // Reuse accepted facts; the server enforces current permission at dispatch.
    // The local owner still runs synchronously immediately before transport.
    const authenticatedFetchChecked = async (url, options = {}, check = null) => {
      const destination = new URL(url, workerOrigin);
      if (destination.origin !== workerOrigin || destination.username || destination.password || destination.hash) fail('DESTINATION_MISMATCH');
      if (options.signal?.aborted) throw new DOMException('The operation was aborted.', 'AbortError');
      const ownedStatus = /^\/v1\/(?:dream\/recommend|prompt|image(?:\/stream)?)\/status$/.test(destination.pathname)
        && negotiatedReuse && activeContext.schemaVersion === 'storefront-context@1';
      const current = ownedStatus ? activeContext : await verify({ force: true });
      const captured = epoch;
      if (check !== null && typeof check !== 'function') fail('INVALID_DISPATCH_CHECK');
      return rawAuthenticatedFetch(url, options, () => {
        requireCurrent(captured);
        if (options.signal?.aborted) throw new DOMException('The operation was aborted.', 'AbortError');
        const result = check?.(current);
        if (result && typeof result.then === 'function') fail('INVALID_DISPATCH_CHECK');
        requireCurrent(captured);
        if (options.signal?.aborted) throw new DOMException('The operation was aborted.', 'AbortError');
      });
    };
    const authenticatedFetch = (url, options = {}) => authenticatedFetchChecked(url, options);
    const originalDescriptor = record => ({ recordId: record.id, source: record.source, createdAt: record.createdAt, expiresAt: record.expiresAt,
      productId: String(record.productId || ''), variantId: String(record.resolution?.shopify_variant_id || ''),
      route: record.source === 'generated' ? 'create' : 'upload', width: record.width, height: record.height });
    const canRecoverOriginal = (proof, record) => Boolean(!disposed && proof && record && !record.composition
      && ['upload', 'generated'].includes(record.source) && record.blob instanceof Blob && record.blob.size
      && ['createdAt', 'expiresAt', 'width', 'height'].every(key => Number.isSafeInteger(record[key]))
      && record.width > 0 && record.height > 0 && record.expiresAt > now() && proof.authorityExpiresAt > now()
      && /^[1-9][0-9]{0,29}$/.test(String(record.productId || ''))
      && /^[1-9][0-9]{0,29}$/.test(String(record.resolution?.shopify_variant_id || ''))
      && JSON.stringify(proof.runtime) === JSON.stringify(runtime)
      && configuredContinuations.sameBaseContext(activeContext, proof.context)
      && JSON.stringify(proof.original) === JSON.stringify(originalDescriptor(record))
      && /^[a-f0-9]{64}\.[0-9]{1,16}\.[A-Za-z0-9_-]{43}$/.test(proof.authorityRef || '')
      && Number(proof.authorityRef.split('.')[1]) === proof.authorityExpiresAt);
    const captureOriginalRecovery = record => {
      if (!usableAuthority() || !verifiedCapabilities.includes('owned-original-recovery@1') || !record || record.composition
        || journeyRecoveryAuthority?.reference !== journeyAuthority.reference) return null;
      const selected = { productId: String(record.productId || ''), variantId: String(record.resolution?.shopify_variant_id || ''), route: record.source === 'generated' ? 'create' : 'upload' };
      if (!/^[1-9][0-9]{0,29}$/.test(selected.variantId) || !getRetainedSelection(selected)
        || !journeyRecoveryAuthority.selectionKeys.includes(selectionKey(selected))) return null;
      const proof = { runtime: copy(runtime), context: copy(journeyRecoveryAuthority.context), authorityRef: journeyAuthority.reference,
        authorityExpiresAt: journeyAuthority.expiresAt, original: originalDescriptor(record) };
      return canRecoverOriginal(proof, record) ? copy(proof) : null;
    };
    const authorizeOriginal = async (record, { signal, check = null } = {}) => {
      const proof = record?.originalRecovery;
      if (!canRecoverOriginal(proof, record) || api.paid?.pending?.()) fail('ORIGINAL_RECOVERY_UNAVAILABLE');
      const captured = epoch;
      const controller = new AbortController();
      const abort = () => controller.abort();
      signal?.addEventListener?.('abort', abort, { once: true });
      controllers.add(controller);
      const timer = setTimeout(abort, 30000);
      const requireOwner = () => {
        requireCurrent(captured);
        if (signal?.aborted || controller.signal.aborted) throw new DOMException('The operation was aborted.', 'AbortError');
        if (!canRecoverOriginal(proof, record) || api.paid?.pending?.() || check && check() !== true) fail('ORIGINAL_RECOVERY_SOURCE_CHANGED');
      };
      try {
        const headers = new Headers({ 'Content-Type': 'application/json', 'X-DIM-Page-Path': pagePath,
          'X-DIM-Journey-Authority': proof.authorityRef, 'X-DIM-Journey-Reuse': '1' });
        // Recovery never establishes or renews a session. Development's
        // existing in-memory bearer must already belong to this tab.
        if (devSession) {
          const token = devSession.getToken?.();
          if (!token) fail('DEV_SIGN_IN_REQUIRED');
          headers.set('Authorization', `Bearer ${token}`);
        }
        requireOwner();
        const response = await fetchImpl(`${workerOrigin}/v1/artwork/original/authorize`, { method: 'POST', headers,
          body: JSON.stringify({ context: proof.context, original: proof.original }), credentials: 'include', redirect: 'error', cache: 'no-store', signal: controller.signal });
        requireOwner();
        const result = await response.json();
        requireOwner();
        if (!response.ok || result?.allowed !== true || result.outputKind !== 'original-image' || result.includesEditorChanges !== false
          || !configuredContinuations.sameAuthorityIdentity(proof.context, result.context)
          || JSON.stringify(result.original) !== JSON.stringify(proof.original)
          || !Number.isSafeInteger(result.expiresAt) || result.expiresAt <= now()
          || result.expiresAt > Math.min(record.expiresAt, proof.authorityExpiresAt)) fail('ORIGINAL_RECOVERY_UNAVAILABLE');
        return Object.freeze({ allowed: true, outputKind: 'original-image', expiresAt: result.expiresAt });
      } finally {
        clearTimeout(timer); controllers.delete(controller); signal?.removeEventListener?.('abort', abort);
      }
    };
    const completedProfile = resolution => ({ releaseId: resolution.release_id, profileKey: resolution.profile_key,
      profileVersion: resolution.profile_version, profileContentChecksum: resolution.profile_content_checksum,
      profileChecksum: resolution.profile_checksum, initialPlacement: resolution.editor_initial_placement || 'fit' });
    const completedDescriptor = record => ({ recordId: record.id, source: 'edited', createdAt: record.createdAt, expiresAt: record.expiresAt,
      productId: String(record.productId || ''), variantId: String(record.resolution?.shopify_variant_id || ''),
      route: record.resolution?.route === 'create_with_ai' ? 'create' : 'upload', treatment: record.composition?.kind === 'words' ? 'words' : 'default',
      width: record.width, height: record.height, pngFingerprint: record.preparedScene.pngFingerprint, profile: completedProfile(record.resolution) });
    const canRecoverCompleted = (proof, record) => Boolean(!disposed && proof?.schemaVersion === 'completed-artwork-recovery@1'
      && record?.source === 'edited' && record.blob instanceof Blob && record.blob.size && record.blob.type === 'image/png' && record.mimeType === 'image/png'
      && ['createdAt', 'expiresAt', 'width', 'height'].every(key => Number.isSafeInteger(record[key]))
      && record.createdAt <= now() && record.expiresAt > now() && record.expiresAt > record.createdAt
      && record.width > 0 && record.height > 0 && record.expiresAt <= proof.authorityExpiresAt && proof.authorityExpiresAt > now()
      && record.width === record.resolution?.editor_output_size_px?.width && record.height === record.resolution?.editor_output_size_px?.height
      && record.width === record.editorState?.outputWidth && record.height === record.editorState?.outputHeight
      && record.preparedScene?.blobRecordId === record.id && /^[a-f0-9]{64}$/.test(record.preparedScene?.pngFingerprint || '')
      && /^[1-9][0-9]{0,29}$/.test(String(record.productId || '')) && /^[1-9][0-9]{0,29}$/.test(String(record.resolution?.shopify_variant_id || ''))
      && JSON.stringify(proof.runtime) === JSON.stringify(runtime) && configuredContinuations.sameBaseContext(activeContext, proof.context)
      && JSON.stringify(proof.artwork) === JSON.stringify(completedDescriptor(record))
      && /^[a-f0-9]{64}\.[0-9]{1,16}\.[A-Za-z0-9_-]{43}$/.test(proof.authorityRef || '')
      && Number(proof.authorityRef.split('.')[1]) === proof.authorityExpiresAt);
    const captureCompletedRecovery = record => {
      if (!usableAuthority() || !verifiedCapabilities.includes('owned-completed-recovery@1') || record?.source !== 'edited'
        || !record.preparedScene || journeyRecoveryAuthority?.reference !== journeyAuthority.reference) return null;
      const artwork = completedDescriptor(record);
      const selected = { productId: artwork.productId, variantId: artwork.variantId, route: artwork.route, treatment: artwork.treatment };
      const retained = getRetainedSelection(selected);
      if (!retained || !journeyRecoveryAuthority.selectionKeys.includes(selectionKey(selected))
        || JSON.stringify(completedProfile(retained.resolution)) !== JSON.stringify(artwork.profile)
        || retained.resolution.editor_output_size_px?.width !== artwork.width || retained.resolution.editor_output_size_px?.height !== artwork.height) return null;
      const proof = { schemaVersion: 'completed-artwork-recovery@1', runtime: copy(runtime), context: copy(journeyRecoveryAuthority.context),
        authorityRef: journeyAuthority.reference, authorityExpiresAt: journeyAuthority.expiresAt, artwork };
      return canRecoverCompleted(proof, record) ? copy(proof) : null;
    };
    const authorizeCompleted = async (record, { signal, check = null } = {}) => {
      const proof = record?.completedRecovery;
      if (!canRecoverCompleted(proof, record) || api.paid?.pending?.()) fail('COMPLETED_RECOVERY_UNAVAILABLE');
      const captured = epoch, controller = new AbortController();
      const abort = () => controller.abort();
      signal?.addEventListener?.('abort', abort, { once: true }); controllers.add(controller);
      const timer = setTimeout(abort, 30000);
      const requireOwner = () => {
        requireCurrent(captured);
        if (signal?.aborted || controller.signal.aborted) throw new DOMException('The operation was aborted.', 'AbortError');
        if (!canRecoverCompleted(proof, record) || api.paid?.pending?.() || check && check() !== true) fail('COMPLETED_RECOVERY_SOURCE_CHANGED');
      };
      try {
        const headers = new Headers({ 'Content-Type': 'application/json', 'X-DIM-Page-Path': pagePath,
          'X-DIM-Journey-Authority': proof.authorityRef, 'X-DIM-Journey-Reuse': '1' });
        if (devSession) {
          const token = devSession.getToken?.(); if (!token) fail('DEV_SIGN_IN_REQUIRED');
          headers.set('Authorization', `Bearer ${token}`);
        }
        requireOwner();
        const response = await fetchImpl(`${workerOrigin}/v1/artwork/completed/authorize`, { method: 'POST', headers,
          body: JSON.stringify({ context: proof.context, artwork: proof.artwork }), credentials: 'include', redirect: 'error', cache: 'no-store', signal: controller.signal });
        requireOwner(); const result = await response.json(); requireOwner();
        if (!response.ok || result?.allowed !== true || result.outputKind !== 'completed-artwork' || result.includesEditorChanges !== true
          || !configuredContinuations.sameAuthorityIdentity(proof.context, result.context) || JSON.stringify(result.artwork) !== JSON.stringify(proof.artwork)
          || !Number.isSafeInteger(result.expiresAt) || result.expiresAt <= now()
          || result.expiresAt > Math.min(record.expiresAt, proof.authorityExpiresAt)) fail('COMPLETED_RECOVERY_UNAVAILABLE');
        return Object.freeze({ allowed: true, outputKind: 'completed-artwork', expiresAt: result.expiresAt });
      } finally { clearTimeout(timer); controllers.delete(controller); signal?.removeEventListener?.('abort', abort); }
    };
    const prepareTransition = async targetPath => {
      const current = await verify({ force: true });
      const grant = continuation.readGrant();
      if (!grant) {
        if (!['primary', 'alias'].includes(current.mode) || current.purpose !== 'customer'
          || typeof targetPath !== 'string' || !/^\/(?!\/)[^?#\\\r\n]*$/.test(targetPath) || targetPath.length > 240) fail('CURRENT_CONTINUATION_REQUIRED');
        return Object.freeze({ targetPath, expiresAt: null, status: 'not-required' });
      }
      return continuation.prepareTransition({ targetPath, context: current, continuation: grant });
    };
    const sceneContext = continuation.provisionalContext || originalContext;
    const scene = scenes?.create({ runtime, storefrontId: sceneContext.storefrontId, purpose: sceneContext.purpose, bindingId: sceneContext.bindingId, contextMode: sceneContext.mode, storage, indexedDB });
    const api = {
      enabled: true, runtime, scene, devSession, continuation,
      get context() { return activeContext; },
      get verified() { return !disposed && verifiedUntil > now(); },
      get display() { return !disposed && verifiedUntil > now() && verifiedDisplay ? copy(verifiedDisplay) : null; },
      features: Object.freeze(copy(config.features || {})),
      get capabilities() { return verifiedCapabilities; },
      fontCatalogue: Object.freeze(copy(config.fontCatalogue || [])),
      get hasJourneyDataReuse() { return Boolean(!disposed && usableAuthority()); },
      get journeyAuthority() { return !disposed && journeyAuthority ? copy(journeyAuthority) : null; },
      get authorityGeneration() { return authorityGeneration; },
      verify, refreshAuthority, authenticatedFetch, authenticatedFetchChecked, getRetainedSelection, retainSelection,
      captureOriginalRecovery, canRecoverOriginal, authorizeOriginal,
      captureCompletedRecovery, canRecoverCompleted, authorizeCompleted,
      async requireAdmission(selection, { signal, currentPermission = false } = {}) {
        const originalSelection = currentPermission ? JSON.stringify(selection) : null;
        const selected = currentPermission ? copy(requestSelection(selection)) : selection;
        const requireSelected = () => {
          if (signal?.aborted) throw new DOMException('The operation was aborted.', 'AbortError');
          if (currentPermission && JSON.stringify(selection) !== originalSelection) fail('CURRENT_ADMISSION_REQUIRED');
        };
        requireSelected();
        await verify({ force: true });
        requireSelected();
        const retained = currentPermission ? null : getRetainedSelection(selection);
        if (retained) return admissionResult(retained);
        const key = `${currentPermission ? 'permission:' : ''}${selectionKey(selection)}`;
        if (!signal && admissionInFlight.has(key)) return admissionInFlight.get(key).then(value => { requireSelected(); return copy(value); });
        const operation = (async () => {
          let captured = epoch;
          const priorContext = activeContext, priorGeneration = authorityGeneration;
          const requestAdmission = () => {
            requireCurrent(captured); requireSelected();
            return boundedJson('/v1/storefront/admit', { method: 'POST', signal, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ context: activeContext, selection: requestSelection(selected),
              ...(verifiedCapabilities.includes('words-only@1') ? { client_capabilities: ['words_only'] } : {}) }) }, captured, currentPermission);
          };
          let response;
          try { response = await requestAdmission(); }
          catch (error) {
            // This explicit admission has no external side effect. Recover only
            // an unconsumed stale permission, never paid or uncertain requests.
            if (!currentPermission || !journeyAuthority || !supportsReuse() || error.status !== 409
              || !['JOURNEY_REVISION_CHANGED', 'JOURNEY_AUTHORITY_EXPIRED'].includes(error.code)) throw error;
            requireCurrent(captured); requireSelected();
            await refreshAuthority({ selection: selected, signal });
            requireCurrent(captured + 1); requireSelected();
            if (authorityGeneration !== priorGeneration + 1 || !configuredContinuations.sameBaseContext(priorContext, activeContext)) fail('CONTEXT_MISMATCH');
            if (!getRetainedSelection(selected)) fail('CURRENT_ADMISSION_REQUIRED');
            captured = epoch;
            response = await requestAdmission(); // A second rejection escapes.
          }
          requireSelected();
          if (response.allowed !== true || !configuredContinuations.sameAuthorityIdentity(activeContext, response.context)) fail('CURRENT_ADMISSION_REQUIRED');
          if (usableAuthority()) retainSelection(selected, response);
          return usableAuthority() ? admissionResult(response) : Object.freeze(copy(response));
        })();
        if (!signal) admissionInFlight.set(key, operation);
        try { return await operation; } finally { if (admissionInFlight.get(key) === operation) admissionInFlight.delete(key); }
      },
      async requireActionPermission(selection, { action, signal } = {}) {
        if (!['native-assignment', 'basket'].includes(action)) fail('INVALID_ACTION_PERMISSION');
        const originalSelection = JSON.stringify(selection);
        const selected = copy(requestSelection(selection));
        const requireSelected = () => {
          if (signal?.aborted) throw new DOMException('The operation was aborted.', 'AbortError');
          if (JSON.stringify(selection) !== originalSelection) fail('CURRENT_ADMISSION_REQUIRED');
        };
        await verify();
        if (!usableAuthority()) fail('CURRENT_JOURNEY_AUTHORITY_REQUIRED');
        let captured = epoch;
        const priorContext = activeContext, priorGeneration = authorityGeneration;
        const requestPermission = () => {
          requireCurrent(captured); requireSelected();
          return boundedJson('/v1/storefront/admit', { method: 'POST', signal, headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ context: activeContext, selection: selected, action }) }, captured);
        };
        let response;
        try { response = await requestPermission(); }
        catch (error) {
          // A compact gate has no external side effect. Only an explicit stale
          // authority rejection can refresh and recheck this unconsumed action;
          // paid requests, revocation and uncertain failures never enter here.
          if (error.status !== 409 || !['JOURNEY_REVISION_CHANGED', 'JOURNEY_AUTHORITY_EXPIRED'].includes(error.code)) throw error;
          requireCurrent(captured); requireSelected();
          await refreshAuthority({ selection: selected, signal });
          requireCurrent(captured + 1); requireSelected();
          if (authorityGeneration !== priorGeneration + 1 || !configuredContinuations.sameBaseContext(priorContext, activeContext)) fail('CONTEXT_MISMATCH');
          if (!getRetainedSelection(selected)) fail('CURRENT_ADMISSION_REQUIRED');
          captured = epoch;
          response = await requestPermission(); // One retry; a second rejection escapes.
        }
        requireSelected();
        if (response.allowed !== true || !configuredContinuations.sameAuthorityIdentity(activeContext, response.context)) fail('CURRENT_ADMISSION_REQUIRED');
        if (response.selection?.productId !== selection.productId || response.selection?.variantId !== selection.variantId) fail('CURRENT_ADMISSION_REQUIRED');
        return admissionResult(response);
      },
      prepareTransition,
      prepareJourneyTransition({ targetPath } = {}) { return prepareTransition(targetPath); },
      invalidate,
      dispose() { disposed = true; epoch++; verifiedUntil = 0; controllers.forEach(controller => controller.abort()); controllers.clear(); continuation.dispose(); devSession?.dispose(); }
    };
    restoreJourney();
    if (paidOperations) api.paid = paidOperations.create({ runtime: api, storage, now });
    return Object.freeze(api);
  }
  return Object.freeze({ create, sameContext });
});
